import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import Image from '../../Utils/Image/image-component';
import SystemDataService from '../../Services/SystemDataService';

// --- Using your Button Components ---
import { ButtonPrimary, ButtonSecondary } from '../Buttons/button-component';


export default function InfoCardsSection({ 
    infoCardsSection, // Received as the data object you provided
    infoCards: passedInfoCards 
}) {
    // 1. Hooks for global state and direction
    const systemData = useSelector((state) => state.systemData || {});
    const { dir } = systemData;

    const [cards, setCards] = useState([]);
    const [visibleCards, setVisibleCards] = useState([]);

    // 2. Destructure the specific section data
    const {
        title,
        subtitle,
        description,
        btnWork,
        btnTalk
    } = infoCardsSection || {};

    // 3. Data Fetching Logic (Default to SystemDataService if props are empty)
    useEffect(() => {
        async function loadCards() {
            if (passedInfoCards && passedInfoCards.length > 0) {
                setCards(passedInfoCards);
            } else {
                try {
                    const data = await SystemDataService.getCardInfo();
                    setCards(data || []);
                } catch (err) {
                    console.error("Failed to fetch info cards:", err);
                }
            }
        }
        loadCards();
    }, [passedInfoCards]);

    // 4. Entrance Animation Logic
    useEffect(() => {
        if (!cards.length) return;
        setVisibleCards(new Array(cards.length).fill(false));
        const timeouts = cards.map((_, index) =>
            setTimeout(() => {
                setVisibleCards((prev) => {
                    const updated = [...prev];
                    updated[index] = true;
                    return updated;
                });
            }, 300 + index * 150)
        );
        return () => timeouts.forEach(clearTimeout);
    }, [cards]);

    return (
        <section dir={dir} className="relative w-full flex flex-col items-center justify-center py-8 px-6 overflow-visible bg-white dark:bg-gray-900">
            <div className="relative z-10 w-full max-w-[1400px] mx-auto flex flex-col items-center">

                {/* --- HEADER SECTION: Structured per LandingData object --- */}
                <div className="text-center mb-20 space-y-4 max-w-4xl">
                    {/* Top Main Title - Uses Secondary Gold */}
                    {title && (
                        <h2 className="text-4xl md:text-5xl lg:text-7xl font-black text-secondary-500 uppercase  leading-none">
                            {title}
                        </h2>
                    )}
                    
                    {/* Subtitle - Uses Primary Blue */}
                    {subtitle && (
                        <h3 className="text-2xl md:text-3xl lg:text-4xl font-bold text-primary-500 leading-tight">
                            {subtitle}
                        </h3>
                    )}

                    {/* Description Paragraph */}
                    {description && (
                        <p className="text-lg md:text-xl text-primary-800 dark:text-gray-400 font-medium max-w-3xl mx-auto leading-relaxed">
                            {description}
                        </p>
                    )}
                </div>

                {/* --- CENTERED CARDS CONTAINER --- */}
                <div className="flex flex-wrap justify-center items-stretch gap-8 lg:gap-10 w-full mb-16">
                    {Array.isArray(cards) && cards.map((card, index) => (
                        <div
                            key={card.id || index}
                            className={`
                                flex flex-col items-center text-center p-6 lg:p-10 
                                w-full sm:w-[calc(50%-1.25rem)] lg:w-[calc(25%-2rem)] min-w-[280px]
                                rounded-[2.5rem] transition-all duration-1000 ease-out 
                                bg-white dark:bg-gray-800 border-4 border-primary-500 shadow-2xl 
                                ${visibleCards[index] ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12'}
                            `}
                        >
                            {/* Icon Wrapper: Solid Primary Blue Border */}
                            <div className="w-28 h-28 sm:w-32 sm:h-32 lg:w-40 lg:h-40 p-4 flex items-center justify-center transition-transform hover:scale-110 duration-500">
                                <Image
                                    src={card.image}
                                    alt={card.title}
                                    className="w-full h-full object-contain"
                                    useSekeleton={true}
                                />
                            </div>

                            {/* Card Heading - Deep Primary Blue */}
                            <h4 className="text-xl lg:text-2xl font-black text-primary-900 dark:text-white mb-4 uppercase leading-tight">
                                {card.title}
                            </h4>

                            {/* Card Description */}
                            {/* <p className="text-primary-800 dark:text-gray-300 font-medium text-sm lg:text-base leading-relaxed">
                                {card.description}
                            </p> */}
                        </div>
                    ))}
                </div>

                {/* --- FOOTER BUTTONS: Uses your Primary and Secondary utility components --- */}
                {(btnWork || btnTalk) && (
                    <div className="flex flex-wrap justify-center gap-6 mt-8">
                        {btnWork && (
                            <ButtonPrimary size="lg" className="uppercase tracking-widest px-12">
                                {btnWork}
                            </ButtonPrimary>
                        )}
                        {btnTalk && (
                            <ButtonSecondary size="lg" className="uppercase tracking-widest px-12">
                                {btnTalk}
                            </ButtonSecondary>
                        )}
                    </div>
                )}
            </div>
        </section>
    );
}